/**
 * ErrorDetailPanel — renders inside the expanded row of an error event.
 *
 * Fetches the full error detail via `eventsApi.getError(id)` lazily on mount.
 * The two error sources have different shapes:
 *   - `source === 'api'` shows status/method/URL/serverError.
 *   - `source === 'errorBoundary'` shows errorName/message + stack/component
 *     stack (collapsed by default; toggleable via a "Stack anzeigen" link).
 */

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { eventsApi } from '../../services/eventsApi';
import type { ErrorEventDetail } from '../../services/types';
import CreateIssueButton from './CreateIssueButton';

interface Props {
  errorId: string;
}

const ErrorDetailPanel: React.FC<Props> = ({ errorId }) => {
  const [detail, setDetail] = useState<ErrorEventDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stackVisible, setStackVisible] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    eventsApi
      .getError(errorId)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Detail konnte nicht geladen werden.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [errorId]);

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gap={1} p={2}>
        <CircularProgress size={20} />
        <Typography variant="body2">Lade Detail …</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }} role="alert">
        {error}
      </Alert>
    );
  }

  if (!detail) return null;

  const api = detail.apiDetail;
  const boundary = detail.boundaryDetail;

  return (
    <Box p={2}>
      <Stack spacing={1.5} divider={<Divider flexItem />}>
        <Box>
          <Typography variant="overline" color="text.secondary">
            Quelle
          </Typography>
          <Box>
            <Chip
              label={detail.source === 'api' ? 'API' : 'UI-Fehler'}
              color={detail.source === 'api' ? 'warning' : 'error'}
              size="small"
            />
          </Box>
        </Box>

        <Box>
          <Typography variant="overline" color="text.secondary">
            Meldung
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {detail.message}
          </Typography>
        </Box>

        {api && (
          <Box>
            <Typography variant="overline" color="text.secondary">
              API-Details
            </Typography>
            <Typography variant="body2">
              {api.method} {api.requestUrl} — Status {api.status}
              {api.requestId && ` · req-id ${api.requestId}`}
            </Typography>
            {api.serverError && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Server: {api.serverError}
              </Typography>
            )}
          </Box>
        )}

        {boundary && (
          <Box>
            <Typography variant="overline" color="text.secondary">
              Stack
            </Typography>
            <Typography variant="body2">
              {boundary.errorName}: {boundary.errorMessage}
            </Typography>
            {(boundary.stack || boundary.componentStack) && (
              <>
                <Link
                  component="button"
                  variant="caption"
                  onClick={() => setStackVisible((v) => !v)}
                  sx={{ mt: 0.5, display: 'inline-block' }}
                >
                  {stackVisible ? 'Stack ausblenden' : 'Stack anzeigen'}
                </Link>
                {stackVisible && (
                  <Box
                    component="pre"
                    sx={{
                      mt: 1,
                      p: 1,
                      bgcolor: 'action.hover',
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {(boundary.stack ?? '') +
                      (boundary.componentStack ? `\n\nComponent stack:${boundary.componentStack}` : '')}
                  </Box>
                )}
              </>
            )}
          </Box>
        )}

        <Box>
          <Typography variant="overline" color="text.secondary">
            Nutzer / Seite
          </Typography>
          <Typography variant="body2">
            {detail.userEmail} ({detail.userId}) — {detail.pageUrl}
          </Typography>
        </Box>

        <Box>
          <Typography variant="overline" color="text.secondary" display="block" gutterBottom>
            GitHub-Issue
          </Typography>
          <CreateIssueButton
            eventKind="error"
            eventId={detail.id}
            existingIssueUrl={detail.githubIssueUrl}
            existingIssueNumber={detail.githubIssueNumber}
          />
        </Box>
      </Stack>
    </Box>
  );
};

export default ErrorDetailPanel;
